import React, { useCallback, useState } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/react";

import { ProposedRoute } from "../../../../../../../types/routeBuilder";
import EditableText from "../../../../../../../components/Inputs/EditableText";
import RouteColorSwatch from "../../../../../../../components/Maps/ActiveRoutes/RouteColorSwatch";
import { resolveRouteColorPalette } from "../../../../../../../components/Maps/ActiveRoutes/routeColor";
import NotesIcon from "../../../../../../../resources/route-builder/notes.svg";
import RefreshIcon from "../../../../../../../resources/route-builder/refresh.svg";
import { DragDropIcon } from "../../../../RoutePage/UI/DragDropIcon";
import { useStopSequence } from "../stops/useStopSequence";
import { useRouteBuilder } from "../../../context/RouteBuilderContext";
import {
  incorporatesTime,
  routeSummaryText,
  shouldShowDropPropertiesHint,
} from "../../../lib/helpers";
import AddPropertyVisit from "../stops/AddPropertyVisit";
import { RemoveRouteButton } from "./RouteActions";
import RouteEndpointsEditor from "./RouteEndpointsEditor";

export const ROUTE_BOARD_COLUMN_SORT_TYPE = "route-board-column";

export default function RouteBoardColumn({
  route,
  selected = false,
  onSelect,
  testId,
  reorderable = false,
}: {
  route: ProposedRoute;
  selected?: boolean;
  onSelect?: () => void;
  testId?: string;
  /** When true, header drag handle reorders columns in list view. */
  reorderable?: boolean;
}) {
  const {
    session,
    dirtyRouteIds,
    renameRoute,
    setRouteColor,
    setRouteInstructions,
    reoptimizeRoute,
    isBusy,
  } = useRouteBuilder();

  const [showInstructions, setShowInstructions] = useState(false);

  // useDraggable (not useSortable): columns nest stop sortables. Optimistic
  // column DOM reorder tries to insertAdjacentElement next to a stop inside
  // the same column → HierarchyRequestError.
  const {
    ref: draggableRef,
    handleRef,
    isDragging,
  } = useDraggable({
    id: `route-board-drag-${route.id}`,
    type: ROUTE_BOARD_COLUMN_SORT_TYPE,
    disabled: !reorderable,
    feedback: "clone",
    data: { routeId: route.id },
  });

  const { ref: droppableRef, isDropTarget } = useDroppable({
    id: `route-board-drop-${route.id}`,
    type: "route-column",
    // Stops and column-reorder drags both land on the column body.
    accept: ["stop", ROUTE_BOARD_COLUMN_SORT_TYPE],
    // Below slots (30) and cards (20); used as a drop target for the column
    // even when it already has stops so list-view drops don't need a 16px gap.
    collisionPriority: 10,
    data: { routeId: route.id },
  });
  // The header above the stops acts as the first slot, so a stop dropped up
  // there goes to the start instead of the end. Beats the column body (10);
  // column-reorder drags still fall through to the column.
  const { ref: headerDropRef } = useDroppable({
    id: `route-board-start-${route.id}`,
    type: "stop-slot",
    accept: "stop",
    collisionPriority: 15,
    data: { routeId: route.id, index: 0 },
  });

  const setSectionRef = useCallback(
    (node: HTMLElement | null) => {
      droppableRef(node);
      if (reorderable) draggableRef(node);
    },
    [reorderable, droppableRef, draggableRef],
  );

  const colorClass = resolveRouteColorPalette(route.color).bg;
  const isDirty = dirtyRouteIds.has(route.id);
  const stopItems = useStopSequence(route.id, route.stops, colorClass);

  return (
    <section
      ref={setSectionRef}
      className={`flex h-full w-[300px] shrink-0 flex-col rounded-xl border bg-slate-50 ${
        selected ? "border-sky-400 shadow-sm" : "border-slate-200"
      } ${isDropTarget ? "ring-2 ring-sky-300" : ""} ${
        isDragging ? "opacity-60 shadow-lg" : ""
      }`}
      data-testid={testId ?? `route-board-column-${route.id}`}
      onClick={onSelect}
    >
      <header
        ref={headerDropRef}
        className="shrink-0 border-b border-slate-200/80 px-3 py-2.5"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {reorderable ? (
              <button
                type="button"
                ref={handleRef}
                data-testid={`board-column-drag-${route.id}`}
                className="shrink-0 cursor-grab touch-none rounded p-0.5 hover:bg-slate-200 active:cursor-grabbing"
                aria-label={`Reorder ${route.name}`}
                onClick={(e) => e.stopPropagation()}
              >
                <DragDropIcon />
              </button>
            ) : null}
            <RouteColorSwatch
              color={route.color}
              size="sm"
              testId={`board-route-color-${route.id}`}
              stopPropagation
              onChange={(next) => setRouteColor(route.id, next)}
            />
            <EditableText
              value={route.name}
              testId={`board-route-name-${route.id}`}
              editLabel="Edit route name"
              className="text-sm"
              onSave={(next) => renameRoute(route.id, next)}
            />
          </div>
          <RemoveRouteButton route={route} />
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
          <span>
            {routeSummaryText(route, {
              incorporateTime: incorporatesTime(session?.inputs),
              includeDistance: true,
            })}
          </span>
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
            onClick={(e) => {
              e.stopPropagation();
              setShowInstructions((v) => !v);
            }}
          >
            <img src={NotesIcon} alt="" className="h-3 w-3" aria-hidden />
            {showInstructions
              ? "Done"
              : route.instructions
                ? "Edit instructions"
                : "Add instructions"}
          </button>
          {isDirty ? (
            <button
              type="button"
              data-testid={`board-reoptimize-route-${route.id}`}
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${
                isDirty
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  : "border-slate-300 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"
              }`}
              disabled={isBusy}
              onClick={(e) => {
                e.stopPropagation();
                void reoptimizeRoute(route.id);
              }}
            >
              <img src={RefreshIcon} alt="" className="h-3 w-3" aria-hidden />
              Re-optimize
            </button>
          ) : null}
        </div>
        {showInstructions && (
          <textarea
            data-testid={`board-route-instructions-${route.id}`}
            className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[11px] text-slate-700"
            rows={2}
            placeholder="Crew instructions…"
            value={route.instructions ?? ""}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setRouteInstructions(route.id, e.target.value)}
          />
        )}
        <RouteEndpointsEditor route={route} />
      </header>

      <ol
        className="flex flex-1 flex-col gap-2 overflow-y-auto p-2"
        data-testid={`route-stops-${route.id}`}
      >
        {stopItems}
        {route.stops.length === 0 &&
        session &&
        shouldShowDropPropertiesHint(session) ? (
          <li
            className="rounded-xl border border-dashed border-slate-300 bg-white/60 p-6 text-center text-sm text-slate-500"
            data-testid="drop-properties-hint"
          >
            Drop properties here
          </li>
        ) : null}
        <li className="list-none">
          <AddPropertyVisit routeId={route.id} />
        </li>
      </ol>
    </section>
  );
}
